import { transitionsToMermaid } from '../visualizer/transitionsToMermaid';
import { getMachines, onMachineRegistered, onMachineUnregistered } from './registry';
function safeSerialize(value) {
  try {
    return JSON.parse(JSON.stringify(value, (_key, v) => {
      if (typeof v === 'function') return '[function]';
      if (typeof v === 'bigint') return '[bigint]';
      return v;
    }));
  } catch (_unused) {
    return '[unserializable]';
  }
}
function serializeTimeline(timeline) {
  return timeline.map(entry => {
    const result = safeSerialize(entry);
    if (typeof result === 'string') {
      return {
        type: entry.type
      };
    }
    return result;
  });
}
function serializeMachine(name, interpreter) {
  return {
    name,
    isAnonymous: interpreter.getInspector().isAnonymous,
    sourceFile: interpreter.getInspector().sourceFile,
    mermaidSource: transitionsToMermaid(interpreter.machine.machineDefinition, {
      activeState: interpreter.value
    }),
    value: interpreter.value,
    context: safeSerialize(interpreter.context),
    timeline: serializeTimeline(interpreter.getInspector().getTimeline()),
    timelineVersion: interpreter.getInspector().timelineVersion,
    running: interpreter.running
  };
}
function post(msg) {
  if (typeof window !== 'undefined') {
    window.postMessage(msg, window.location.origin);
  }
}

// Module-level bridge instance — set up once per page load, not per machine.
let bridgeTeardown = null;
export function enableFsmDevTools() {
  if (bridgeTeardown !== null) return bridgeTeardown;
  const subscriptions = new Map();
  const stopCleanups = new Map();
  const startCleanups = new Map();
  const getSnapshot = () => Array.from(getMachines().entries()).filter(([, i]) => i.running).map(([n, i]) => serializeMachine(n, i));
  function subscribeToMachine(name, interpreter) {
    var _subscriptions$get, _stopCleanups$get, _startCleanups$get;
    // When the registry replaces a stopped machine in-place (HMR / StrictMode
    // remount), this function is called again for the same name. Clean up the
    // previous interpreter's subscription first so it doesn't leak.
    (_subscriptions$get = subscriptions.get(name)) === null || _subscriptions$get === void 0 || _subscriptions$get();
    subscriptions.delete(name);
    (_stopCleanups$get = stopCleanups.get(name)) === null || _stopCleanups$get === void 0 || _stopCleanups$get();
    stopCleanups.delete(name);
    (_startCleanups$get = startCleanups.get(name)) === null || _startCleanups$get === void 0 || _startCleanups$get();
    startCleanups.delete(name);
    const unsubscribe = interpreter.subscribe(() => {
      post({
        source: 'FSM_DT_CLIENT',
        type: 'FSM_DT_TRANSITION',
        name,
        isAnonymous: interpreter.getInspector().isAnonymous,
        sourceFile: interpreter.getInspector().sourceFile,
        value: interpreter.value,
        mermaidSource: transitionsToMermaid(interpreter.machine.machineDefinition, {
          activeState: interpreter.value
        }),
        context: safeSerialize(interpreter.context),
        timeline: serializeTimeline(interpreter.getInspector().getTimeline()),
        timelineVersion: interpreter.getInspector().timelineVersion,
        running: interpreter.running
      });
    });

    // Notify the panel when the machine stops. The microtask delay matches the
    // registry so restart() has already re-started before we check running.
    const removeStopCallback = interpreter.onStop(() => {
      void Promise.resolve().then(() => {
        // Guard against a replaced interpreter evicting the new one's subscription.
        // If the registry has already swapped in a new interpreter under this name,
        // subscriptions.get(name) will point to the new unsubscribe — not ours.
        if (!interpreter.running && subscriptions.get(name) === unsubscribe) {
          var _stopCleanups$get2;
          unsubscribe();
          subscriptions.delete(name);
          (_stopCleanups$get2 = stopCleanups.get(name)) === null || _stopCleanups$get2 === void 0 || _stopCleanups$get2();
          stopCleanups.delete(name);
          post({
            source: 'FSM_DT_CLIENT',
            type: 'FSM_DT_UNREGISTERED',
            name
          });
        }
      });
    });
    subscriptions.set(name, unsubscribe);
    stopCleanups.set(name, removeStopCallback);
    if (!interpreter.running) {
      // A machine may already be registered before the bridge is enabled. In
      // that case there is no registered event to attach this hook, so install
      // it while subscribing as well as from the registration callback below.
      const removeStartCallback = interpreter.onStart(() => {
        removeStartCallback();
        startCleanups.delete(name);
        if (subscriptions.get(name) !== unsubscribe) return;
        post({
          source: 'FSM_DT_CLIENT',
          type: 'FSM_DT_REGISTERED',
          machine: serializeMachine(name, interpreter)
        });
      });
      startCleanups.set(name, removeStartCallback);
    }
  }
  for (const [name, interpreter] of getMachines()) {
    subscribeToMachine(name, interpreter);
  }
  const offRegistered = onMachineRegistered((name, interpreter) => {
    subscribeToMachine(name, interpreter);
    if (interpreter.running) {
      post({
        source: 'FSM_DT_CLIENT',
        type: 'FSM_DT_REGISTERED',
        machine: serializeMachine(name, interpreter)
      });
    }
  });
  const offUnregistered = onMachineUnregistered(name => {
    var _subscriptions$get2, _stopCleanups$get3, _startCleanups$get2;
    (_subscriptions$get2 = subscriptions.get(name)) === null || _subscriptions$get2 === void 0 || _subscriptions$get2();
    subscriptions.delete(name);
    (_stopCleanups$get3 = stopCleanups.get(name)) === null || _stopCleanups$get3 === void 0 || _stopCleanups$get3();
    stopCleanups.delete(name);
    (_startCleanups$get2 = startCleanups.get(name)) === null || _startCleanups$get2 === void 0 || _startCleanups$get2();
    startCleanups.delete(name);
    post({
      source: 'FSM_DT_CLIENT',
      type: 'FSM_DT_UNREGISTERED',
      name
    });
  });
  const handlePanelMessage = event => {
    const data = event.data;
    if ((data === null || data === void 0 ? void 0 : data.source) === 'FSM_DT_PANEL' && (data === null || data === void 0 ? void 0 : data.type) === 'FSM_DT_INIT') {
      // Respond with only running machines so the panel doesn't show stale
      // stopped entries (e.g. waitToStart:true machines from useCreateMachine).
      post({
        source: 'FSM_DT_CLIENT',
        type: 'FSM_DT_SNAPSHOT',
        machines: Array.from(getMachines().entries()).filter(([, i]) => i.running).map(([n, i]) => serializeMachine(n, i))
      });
    }
  };
  if (typeof window !== 'undefined') {
    window.addEventListener('message', handlePanelMessage);
    // Direct-call hook for chrome.devtools.inspectedWindow.eval() — lets the
    // DevTools panel fetch a snapshot without going through the content script.
    window.__FSM_GET_SNAPSHOT = getSnapshot;
  }

  // Push an initial snapshot of running machines. The snapshot is filtered to
  // running-only because useCreateMachine creates a waitToStart:true machine
  // during render (running:false) before the useEffect creates the real one.
  // Including stopped machines here would flash "stopped" in the panel.
  const runningOnInit = Array.from(getMachines().entries()).filter(([, i]) => i.running);
  if (runningOnInit.length > 0) {
    post({
      source: 'FSM_DT_CLIENT',
      type: 'FSM_DT_SNAPSHOT',
      machines: runningOnInit.map(([n, i]) => serializeMachine(n, i))
    });
  }
  bridgeTeardown = () => {
    offRegistered();
    offUnregistered();
    subscriptions.forEach(unsub => unsub());
    subscriptions.clear();
    stopCleanups.forEach(cleanup => cleanup());
    stopCleanups.clear();
    startCleanups.forEach(cleanup => cleanup());
    startCleanups.clear();
    if (typeof window !== 'undefined') {
      window.removeEventListener('message', handlePanelMessage);
      const globals = window;
      if (globals.__FSM_GET_SNAPSHOT === getSnapshot) {
        delete globals.__FSM_GET_SNAPSHOT;
      }
    }
    bridgeTeardown = null;
  };
  return bridgeTeardown;
}
export function ensureFsmDevToolsBridge() {
  enableFsmDevTools();
}